import React, { useState, useRef, useCallback, useEffect } from "react";
import { Label, TextInput } from "flowbite-react";
import type { CropRegion } from "../types";

interface CropSelectorProps {
  imageUrl: string | null;
  cropRegion: CropRegion;
  onChange: (region: CropRegion) => void;
}

const CropSelector: React.FC<CropSelectorProps> = ({
  imageUrl,
  cropRegion,
  onChange,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const getScales = useCallback(() => {
    if (!imgRef.current || !naturalSize) return { x: 1, y: 1 };
    return {
      x: naturalSize.width / imgRef.current.clientWidth,
      y: naturalSize.height / imgRef.current.clientHeight,
    };
  }, [naturalSize]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !dragStart || !imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const curX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const curY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
      const scales = getScales();

      onChange({
        x: Math.round(Math.min(dragStart.x, curX) * scales.x),
        y: Math.round(Math.min(dragStart.y, curY) * scales.y),
        width: Math.round(Math.abs(curX - dragStart.x) * scales.x),
        height: Math.round(Math.abs(curY - dragStart.y) * scales.y),
      });
    },
    [isDragging, dragStart, onChange, getScales]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleFieldChange = useCallback((field: keyof CropRegion, value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      onChange({ ...cropRegion, [field]: num });
    }
  }, [cropRegion, onChange]);

  const hasRegion = cropRegion.width > 0 && cropRegion.height > 0;
  const scales = getScales();
  const overlayLeft = cropRegion.x / scales.x;
  const overlayTop = cropRegion.y / scales.y;
  const overlayWidth = cropRegion.width / scales.x;
  const overlayHeight = cropRegion.height / scales.y;

  const fieldLabels: Record<keyof CropRegion, string> = {
    x: "X",
    y: "Y",
    width: "幅",
    height: "高さ",
  };

  return (
    <div className="space-y-4">
      <div className="relative inline-block select-none">
        {imageUrl ? (
          <>
            <img
              ref={imgRef}
              src={imageUrl}
              alt="サンプル画像"
              className="max-w-full border rounded block"
              style={{ maxHeight: "400px", cursor: "crosshair" }}
              draggable={false}
              onLoad={() => {
                if (imgRef.current) {
                  setNaturalSize({
                    width: imgRef.current.naturalWidth,
                    height: imgRef.current.naturalHeight,
                  });
                }
              }}
              onMouseDown={handleMouseDown}
            />
            {hasRegion && (
              <div
                className="absolute pointer-events-none border-2 border-blue-400"
                style={{
                  left: overlayLeft,
                  top: overlayTop,
                  width: overlayWidth,
                  height: overlayHeight,
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                }}
              />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded">
            <p className="text-sm text-gray-500">
              画像がまだアップロードされていません
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {(["x", "y", "width", "height"] as const).map((field) => (
          <div key={field}>
            <Label htmlFor={`crop-${field}`} value={fieldLabels[field]} />
            <div className="flex items-center gap-1 mt-1">
              <TextInput
                id={`crop-${field}`}
                type="number"
                value={cropRegion[field]}
                min={0}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className="flex-1"
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">px</span>
            </div>
          </div>
        ))}
      </div>

      {naturalSize && (
        <p className="text-xs text-gray-400">
          画像サイズ: {naturalSize.width} × {naturalSize.height} px
        </p>
      )}
    </div>
  );
};

export default CropSelector;
